import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { X } from 'lucide-react';
import { useCan } from '../../hooks/useCan';

const storageKey = (subjectId: string) => `lophoc.templateHint.${subjectId}`;

/**
 * Spec §13.3: one muted line under the D1 footer summary when the subject has
 * no template and the user can create one. Dismissed per subject in
 * localStorage; never shown to MEMBER; the caller hides it once a template
 * exists.
 */
export function TemplateHintLine({ subjectId }: { subjectId: string }) {
  const { t } = useTranslation('progress');
  const canManage = useCan('manage:subjects');
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(storageKey(subjectId)) === '1'; } catch { return false; }
  });
  if (!canManage || dismissed) return null;
  const dismiss = () => {
    try { localStorage.setItem(storageKey(subjectId), '1'); } catch { /* private mode: dismiss for this render only */ }
    setDismissed(true);
  };
  return (
    <div className="flex items-center gap-2 border-b border-surface-100 px-5 py-2 text-[12px] text-surface-400">
      <span className="min-w-0 truncate">
        {t('notes.templateHint.text')}{' '}
        <Link to={`/subjects/${subjectId}`} className="font-medium text-brand-600 hover:underline">
          {t('notes.templateHint.cta')}
        </Link>
      </span>
      <button type="button" onClick={dismiss} aria-label={t('notes.templateHint.dismiss')} className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center text-surface-400 hover:text-surface-600">
        <X className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  );
}
